import React, { useState, useEffect } from "react";
import { 
  Download, 
  Lock, 
  AlertTriangle, 
  Clock, 
  FileVideo,
  Smartphone,
  CheckCircle,
  XCircle,
  Loader2,
  HardDrive
} from "lucide-react";
import "./CustomerDownload.css";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const NSAS_LOGO = "https://customer-assets.emergentagent.com/job_preflight-weather/artifacts/t3bj072m_NSAS-RGB-zilver-baseline.png";

// Format file size
const formatFileSize = (bytes) => {
  if (!bytes || bytes < 0) return "Unknown";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

// Detect mobile device
const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
         (window.innerWidth <= 768);
};

const CustomerDownload = () => {
  const [pin, setPin] = useState("");
  const [validated, setValidated] = useState(false);
  const [videos, setVideos] = useState([]);
  const [expiresAt, setExpiresAt] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expired, setExpired] = useState(false);
  
  // Mobile approval state
  const [mobileRequests, setMobileRequests] = useState({}); // filename -> {requestId, status}
  const [pollingIntervals, setPollingIntervals] = useState({});
  
  const isMobile = isMobileDevice();

  const handleValidatePin = async () => {
    if (pin.length !== 6) {
      setError("Please enter a 6-digit PIN code");
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/customer-download/validate-pin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin })
      });
      
      if (!response.ok) {
        const data = await response.json();
        if (response.status === 403 && data.detail === "Access expired") {
          setExpired(true);
          setError(null);
        } else {
          setError(data.detail || "Invalid PIN code");
        }
        setValidated(false);
        return;
      }
      
      const data = await response.json();
      setVideos(data.videos || []);
      setExpiresAt(data.expires_at);
      setValidated(true);
      
    } catch (err) {
      setError("Failed to connect to server");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (video) => {
    if (isMobile) {
      // Mobile: Request approval
      await requestMobileApproval(video);
    } else {
      // Desktop: Direct download
      try {
        const response = await fetch(`${API_URL}/api/customer-download/video-url/${pin}/${video.filename}`);
        if (!response.ok) {
          const data = await response.json();
          setError(data.detail || "Download failed");
          return;
        }
        
        const data = await response.json();
        
        // Trigger download
        const link = document.createElement('a');
        link.href = data.download_url;
        link.download = video.filename;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
      } catch (err) {
        setError("Download failed");
      }
    }
  };

  const requestMobileApproval = async (video) => {
    try {
      const response = await fetch(`${API_URL}/api/customer-download/request-mobile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin, filename: video.filename })
      });
      
      if (!response.ok) {
        const data = await response.json();
        setError(data.detail || "Request failed");
        return;
      }
      
      const data = await response.json();
      
      // Update state with pending request
      setMobileRequests(prev => ({
        ...prev,
        [video.filename]: {
          requestId: data.request_id,
          status: 'pending'
        }
      }));
      
      // Start polling for approval
      startPolling(video.filename, data.request_id);
      
    } catch (err) {
      setError("Request failed");
    }
  };

  const startPolling = (filename, requestId) => {
    // Clear any existing interval for this file
    if (pollingIntervals[filename]) {
      clearInterval(pollingIntervals[filename]);
    }
    
    const intervalId = setInterval(async () => {
      try {
        const response = await fetch(`${API_URL}/api/customer-download/check-approval/${requestId}`);
        const data = await response.json();
        
        if (data.status === 'approved') {
          // Stop polling
          clearInterval(intervalId);
          setPollingIntervals(prev => {
            const newIntervals = { ...prev };
            delete newIntervals[filename];
            return newIntervals;
          });
          
          // Update state
          setMobileRequests(prev => ({
            ...prev,
            [filename]: {
              ...prev[filename],
              status: 'approved',
              downloadUrl: data.download_url
            }
          }));
          
        } else if (data.status === 'denied' || data.status === 'expired') {
          // Stop polling
          clearInterval(intervalId);
          setPollingIntervals(prev => {
            const newIntervals = { ...prev };
            delete newIntervals[filename];
            return newIntervals;
          });
          
          // Update state
          setMobileRequests(prev => ({
            ...prev,
            [filename]: {
              ...prev[filename],
              status: data.status
            }
          }));
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, 3000); // Poll every 3 seconds
    
    setPollingIntervals(prev => ({
      ...prev,
      [filename]: intervalId
    }));
  };

  const handleMobileDownload = (video) => {
    const request = mobileRequests[video.filename];
    if (request && request.status === 'approved' && request.downloadUrl) {
      const link = document.createElement('a');
      link.href = request.downloadUrl;
      link.download = video.filename;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      Object.values(pollingIntervals).forEach(intervalId => {
        clearInterval(intervalId);
      });
    };
  }, [pollingIntervals]);

  // Calculate remaining time
  const getRemainingTime = () => {
    if (!expiresAt) return null;
    const expires = new Date(expiresAt);
    const now = new Date();
    const diff = expires - now;
    
    if (diff <= 0) return "Expired";
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    return `${hours}h ${minutes}m remaining`;
  };

  // Expired state
  if (expired) {
    return (
      <div className="customer-download-page">
        <div className="customer-download-container">
          <div className="nsas-logo-container">
            <img src={NSAS_LOGO} alt="NSAS" className="nsas-logo" />
          </div>
          
          <div className="expired-state">
            <div className="expired-icon">
              <Clock size={64} />
            </div>
            <h1>Access Expired</h1>
            <p>Your download access has expired.</p>
            <p className="contact-info">Please contact NSAS for assistance.</p>
          </div>
        </div>
      </div>
    );
  }

  // PIN entry state
  if (!validated) {
    return (
      <div className="customer-download-page">
        <div className="customer-download-container">
          <div className="nsas-logo-container">
            <img src={NSAS_LOGO} alt="NSAS" className="nsas-logo" />
          </div>
          
          <div className="pin-entry-section">
            <div className="lock-icon">
              <Lock size={48} />
            </div>
            <h1>Video Download Portal</h1>
            <p>Enter your 6-digit PIN code to access your videos</p>
            
            <div className="pin-input-wrapper">
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={pin}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '');
                  setPin(value);
                  setError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && pin.length === 6) {
                    handleValidatePin();
                  }
                }}
                placeholder="000000"
                className="pin-input"
                autoFocus
              />
            </div>
            
            {error && (
              <div className="error-message">
                <AlertTriangle size={16} />
                {error}
              </div>
            )}
            
            <button 
              className="validate-btn"
              onClick={handleValidatePin}
              disabled={pin.length !== 6 || loading}
            >
              {loading ? (
                <>
                  <Loader2 size={20} className="spinning" />
                  Validating...
                </>
              ) : (
                <>
                  <Lock size={20} />
                  Access Videos
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Video list state
  return (
    <div className="customer-download-page">
      <div className="customer-download-container validated">
        <div className="nsas-logo-container">
          <img src={NSAS_LOGO} alt="NSAS" className="nsas-logo" />
        </div>
        
        <div className="download-header">
          <h1>Your Videos</h1>
          {expiresAt && (
            <div className="time-remaining">
              <Clock size={16} />
              {getRemainingTime()}
            </div>
          )}
        </div>
        
        {isMobile && (
          <div className="mobile-notice">
            <Smartphone size={18} />
            <span>Mobile device detected. Downloads require admin approval.</span>
          </div>
        )}
        
        {error && (
          <div className="error-message">
            <AlertTriangle size={16} />
            {error}
          </div>
        )}
        
        <div className="video-list">
          {videos.map((video, index) => {
            const mobileRequest = mobileRequests[video.filename];
            const isPending = mobileRequest?.status === 'pending';
            const isApproved = mobileRequest?.status === 'approved';
            const isDenied = mobileRequest?.status === 'denied';
            const isExpiredRequest = mobileRequest?.status === 'expired';
            
            return (
              <div key={index} className="video-item">
                <div className="video-icon">
                  <FileVideo size={32} />
                </div>
                <div className="video-info">
                  <div className="video-filename">{video.filename}</div>
                  <div className="video-size">
                    <HardDrive size={14} />
                    {formatFileSize(video.size)}
                  </div>
                </div>
                <div className="video-action">
                  {isMobile ? (
                    // Mobile download flow
                    isPending ? (
                      <button className="download-btn pending" disabled>
                        <Loader2 size={18} className="spinning" />
                        Awaiting Approval
                      </button>
                    ) : isApproved ? (
                      <button 
                        className="download-btn approved"
                        onClick={() => handleMobileDownload(video)}
                      >
                        <CheckCircle size={18} />
                        Download Now
                      </button>
                    ) : isDenied ? (
                      <button className="download-btn denied" disabled>
                        <XCircle size={18} />
                        Denied
                      </button>
                    ) : isExpiredRequest ? (
                      <button 
                        className="download-btn"
                        onClick={() => handleDownload(video)}
                      >
                        <Download size={18} />
                        Request Again
                      </button>
                    ) : (
                      <button 
                        className="download-btn"
                        onClick={() => handleDownload(video)}
                      >
                        <Download size={18} />
                        Request Download
                      </button>
                    )
                  ) : (
                    // Desktop direct download
                    <button 
                      className="download-btn"
                      onClick={() => handleDownload(video)}
                    >
                      <Download size={18} />
                      Download
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        
        <div className="download-footer">
          <p>Videos will be available for 36 hours from first access.</p>
        </div>
      </div>
    </div>
  );
};

export default CustomerDownload;
